import React from 'react'
import { apiCall, ApiConfig } from '../../../config/api'
import { eWorkOrderState } from '../../../enums/eWorkOrderState'
import { useZones } from '../../../hooks'
import {
  PickingRouteStatusItem,
  WorkOrderCheckInOut,
  ZoneStatusItem
} from '../../../interfaces/WorkOrder/WorkOrder'
import { generateErrorFromResponse } from '../../../utils/errorHelper'
import { useCheck } from '../context/useCheck'
import { checkActions } from './checkActions'

/*
 * Copyright 2022-07-26 por Alfonso Moya Ecomsur Holding S.A ©
 * Detalle: se crea CheckBusiness por cambio a useReducer + Context
 */

export const checkBusiness = () => {
  const resetState = () => {
    dispatch(setSearchedId(''))
    dispatch(setError(undefined))
    dispatch(setWasRegistered(false))
    dispatch(setDriver(''))
    dispatch(setShowCheckInDescription(false))
    dispatch(setShowCheckInDescription(false))
    dispatch(setWorkOrderSearchCheckInOut(undefined))
    dispatch(setWorkOrderDispatcher(''))
    dispatch(setCheckInWorkOrder(undefined))
    resetPickingStatusState()
  }

  const { state, dispatch } = useCheck()

  const { workOrderSearchCheckInOut, isWorkOrderId } = state

  const {
    setSearchedId,
    setWorkOrderDispatcher,
    setLoading,
    setWorkOrderSearchCheckInOut,
    setDriver,
    setWasRegistered,
    setError,
    setShowModal,
    setCheckInWorkOrder,
    setShowCheckInDescription,
    setShowPickingRouteStatus,
    setZonesStatus,
    setLoadingZones
  } = checkActions()

  const getWorkOrderIdFromPickingRouteId = async (pickingRouteId: number) => {
    const requestUri = `${ApiConfig.PickingRouteApi}/${pickingRouteId}`

    try {
      const { data, status } = await apiCall('GET', requestUri)

      if (status === 204) {
        setError('No hay elementos que coincidan con la búsqueda')
        return 0
      }

      if (status === 200) {
        return data.workOrderId
      }
    } catch (response) {
      const message = generateErrorFromResponse(response as any)
      dispatch(setError(message))
    }

    return 0
  }

  const resetPickingStatusState = () => {
    dispatch(setShowPickingRouteStatus(false))
    dispatch(setZonesStatus([]))
  }

  const validateWorkOrderStatusFromWorkOrderState = async (
    workOrderStateId: number,
    workOrderId: number
  ) => {
    switch (workOrderStateId) {
      case eWorkOrderState.CREATED:
      case eWorkOrderState.ON_MASSIVE_PROCESS:
      case eWorkOrderState.ON_PROCESS:
        dispatch(setError('OT no cuenta con Rutas de Picking'))
        break
      case eWorkOrderState.AVAILABLE_FOR_PICKING: // DISPONIBLE PARA PICKING
      case eWorkOrderState.ON_PICKING_ROUTE: // EN RUTA DE PICKING
      case eWorkOrderState.PICKING_FINISHED: // PICKING FINALIZADO
      case eWorkOrderState.ON_VALIDATION: // EN VALIDACIÓN
        dispatch(
          setError(
            'Existen rutas de picking sin validar en las siguientes zonas'
          )
        )
        getWorkOrderZones(workOrderId)
        dispatch(setShowPickingRouteStatus(true))
        break
      case eWorkOrderState.VALIDATION_FINISHED:
      case eWorkOrderState.ON_PREDISPATCH: {
        await getWorkOrder(workOrderId)
        break
      }
      case eWorkOrderState.DISPATCHED:
        dispatch(setError('OT ya se encuentra despachada'))
        break
      case eWorkOrderState.FINISHED:
        dispatch(setError('OT se encuentra Finalizada'))
        break
      default:
        dispatch(
          setError('Estado Desconocido. Favor contactar al administrador')
        )
        break
    }
  }

  const {
    zonesState: { zones },
    getZones
  } = useZones()

  React.useEffect(() => {
    if (zones?.length === 0) {
      getZones()
    }
  }, [])

  const getWorkOrderStatus = async (searchId: number) => {
    resetPickingStatusState()
    dispatch(setLoading(true))

    let workOrderId = searchId

    if (!isWorkOrderId) {
      workOrderId = await getWorkOrderIdFromPickingRouteId(searchId)
    }

    const requestUri = `${ApiConfig.WorkOrderStatusApi}/${workOrderId}`

    apiCall('GET', requestUri)
      .then(async ({ status, data }) => {
        if (status === 204) {
          dispatch(setError('No hay elementos que coincidan con la búsqueda'))
        }

        if (status === 200) {
          await validateWorkOrderStatusFromWorkOrderState(
            data.workOrderStateId,
            workOrderId
          )
        }
      })
      .catch((response) => {
        const message = generateErrorFromResponse(response)
        dispatch(setError(message))
      })
      .finally(() => dispatch(setLoading(false)))
  }

  const getWorkOrder = async (workOrderId: number) => {
    const workOrderUrl = `${ApiConfig.DispatchOt}/GetWorkOrderOutbound?workOrderId=${workOrderId}`

    dispatch(setLoading(true))

    await apiCall('GET', workOrderUrl)
      .then((response) => {
        dispatch(setWorkOrderSearchCheckInOut(response.data))
        let carrierName: string = ''
        let carrierCode: string = ''
        if (response.data.workOrderOutbound != null) {
          const { name, code } = response.data.workOrderOutbound.carrier
          carrierName = name
          carrierCode = code
        }
        dispatch(
          setDriver(
            carrierName === carrierCode
              ? carrierCode
              : `${carrierName} (${carrierCode})`
          )
        )
        dispatch(setWasRegistered(response.data.workOrderOutbound != null))
        dispatch(setError(undefined))
      })
      .catch((response) => {
        const message = generateErrorFromResponse(response)
        dispatch(setError(message))
      })
      .finally(() => dispatch(setLoading(false)))
  }

  const createCheckin = (body: WorkOrderCheckInOut) => {
    if (
      workOrderSearchCheckInOut?.workOrderOutbound !== null &&
      workOrderSearchCheckInOut?.workOrderOutbound?.checkIn !== ''
    ) {
      dispatch(setWasRegistered(true))
    }

    if (
      body.carrierCode !== body.oldcarrierCode &&
      body.oldcarrierCode !== '' &&
      body.carrierCode !== ''
    ) {
      dispatch(setShowModal(true))
    } else {
      dispatch(setLoading(true))
      apiCall('POST', `${ApiConfig.DispatchOt}/CheckInOut`, body)
        .then((response) => {
          dispatch(setCheckInWorkOrder(response.data))
          dispatch(setShowCheckInDescription(true))
          dispatch(setDriver(response.data.carrierName))
          return response.data
        })
        .catch((response) => {
          const message = generateErrorFromResponse(response)
          dispatch(setError(message))
        })
        .finally(() => {
          dispatch(setLoading(false))
          dispatch(setWorkOrderDispatcher(''))
        })
    }
  }

  const createCheckout = async (body: WorkOrderCheckInOut) => {
    dispatch(setLoading(true))
    await apiCall('POST', `${ApiConfig.DispatchOt}/CheckInOut`, body)
      .then((response) => {
        dispatch(setCheckInWorkOrder(response.data))
        dispatch(setDriver(response.data.carrierName))

        return response.data
      })
      .catch((response) => {
        const message = generateErrorFromResponse(response)
        dispatch(setError(message))
      })
      .finally(() => {
        dispatch(setShowModal(false))
        dispatch(setShowCheckInDescription(true))
        dispatch(setLoading(false))
      })
  }

  const getWorkOrderZones = async (workOrderId: number) => {
    dispatch(setLoadingZones(true))

    const requestUri = `${ApiConfig.PickingRouteStatusApi}?WorkOrderId=${workOrderId}`

    await apiCall('GET', requestUri)
      .then(({ data, status }) => {
        if (status === 204) {
          dispatch(setError('No hay rutas de picking creadas para OT'))
        }

        if (status === 200) {
          const zonesStatus = groupZonesStatus(data)
          dispatch(setZonesStatus(zonesStatus))
        }
      })
      .catch((response) => {
        const message = generateErrorFromResponse(response)
        dispatch(setError(message))
      })
      .finally(() => dispatch(setLoadingZones(false)))
  }

  const groupZonesStatus = (
    pickingRouteStatus: PickingRouteStatusItem[]
  ): ZoneStatusItem[] => {
    const zonesStatus: ZoneStatusItem[] = pickingRouteStatus.map((ps) => {
      const zone = zones?.find((z) => z.zoneId === ps.zoneId)
      return {
        zoneName: zone?.zoneName || 'Zona Indefinida',
        isValidated: ps.validationStatusId === 3
      }
    })

    return zonesStatus
  }

  return {
    resetState,
    getWorkOrder,
    createCheckin,
    createCheckout,
    getWorkOrderStatus,
    getWorkOrderZones
  }
}
